import { Controller } from "@hotwired/stimulus"
import { WeatherParticles, sceneFor, familyForCode } from "../weather_scenery"

// Drives one weather scene: applies the family/day-night palette classes to the
// backdrop, builds the star field, owns the single rAF loop that ticks the
// canvas particle engine, fires lightning for thunderstorms, and runs a light
// pointer parallax on the sky layers. Mirrors the ergonomics of
// assistant_hero_controller (visibility pause, prefers-reduced-motion, seeded
// stars) but is entirely weather-owned — it imports nothing from home/zen.
//
// Public API (called by weather_app_controller): apply({ family, isDay, wind }).
export default class extends Controller {
    static targets = ["backdrop", "canvas", "stars", "lightning", "sun", "layer"]

    connect() {
        this._motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)")
        this._reduced = this._motionQuery.matches
        this._hidden = document.hidden
        this._raf = null
        this._last = 0
        this._family = "clear"
        this._isDay = true
        this._wind = 0
        this._lightningTimer = null
        this._cx = 0
        this._cy = 0
        this._tx = 0
        this._ty = 0
        this._parallaxRaf = null

        this._onVisibility = this._onVisibility.bind(this)
        this._onMotion = this._onMotion.bind(this)
        this._onResize = this._onResize.bind(this)
        this._onMove = this._onMove.bind(this)
        this._tick = this._tick.bind(this)
        this._parallaxTick = this._parallaxTick.bind(this)

        if (this.hasCanvasTarget) this._particles = new WeatherParticles(this.canvasTarget)
        this._buildStars()

        document.addEventListener("visibilitychange", this._onVisibility)
        this._motionQuery.addEventListener("change", this._onMotion)
        window.addEventListener("resize", this._onResize, { passive: true })
        if (!this._reduced) this.element.addEventListener("pointermove", this._onMove)

        // Initial scene from data attributes (server first paint).
        this.apply({
            family: this.data.get("family") || "clear",
            isDay: this.data.get("isDay") !== "false",
            wind: parseFloat(this.data.get("wind") || "0")
        })
    }

    disconnect() {
        document.removeEventListener("visibilitychange", this._onVisibility)
        this._motionQuery.removeEventListener("change", this._onMotion)
        window.removeEventListener("resize", this._onResize)
        this.element.removeEventListener("pointermove", this._onMove)
        this._stopLoop()
        this._clearLightning()
        if (this._parallaxRaf) cancelAnimationFrame(this._parallaxRaf)
    }

    // --- Public: switch the scene (family + day/night + wind) ---
    apply({ family, isDay = true, wind = 0 } = {}) {
        if (!family) return
        this._family = family
        this._isDay = isDay
        this._wind = wind

        this._applyClasses()
        this._toggleStars()
        if (this._particles) {
            this._particles.configure({ family, wind, isDay })
        }
        this._armLightning()
        this._syncLoop()
    }

    _applyClasses() {
        const bd = this.hasBackdropTarget ? this.backdropTarget : this.element
        bd.className = bd.className
            .split(/\s+/)
            .filter((c) => !c.startsWith("wx-scene--"))
            .join(" ")
        bd.classList.add("wx-scene")
        bd.classList.add(`wx-scene--${this._family}`)
        bd.classList.add(this._isDay ? "wx-scene--day" : "wx-scene--night")
        if (this._wind >= 24) bd.classList.add("wx-scene--windy")
        bd.classList.toggle("wx-scene--reduced", this._reduced)
    }

    _toggleStars() {
        if (!this.hasStarsTarget) return
        const scene = sceneFor(this._family)
        this.starsTarget.hidden = !(scene.stars && !this._isDay)
    }

    // --- Seeded star field (weather-owned; independent of the home star seed) ---
    _buildStars() {
        if (!this.hasStarsTarget) return
        this.starsTarget.querySelectorAll(".wx-star").forEach((s) => s.remove())
        const frag = document.createDocumentFragment()
        for (let i = 0; i < 40; i++) {
            const star = document.createElement("span")
            star.className = "wx-star"
            const size = Math.random() < 0.2 ? 2 : 1
            star.style.cssText = `top:${Math.floor(Math.random() * 62)}%;left:${Math.floor(Math.random() * 101)}%;width:${size}px;height:${size}px;animation-delay:${(Math.random() * 6).toFixed(2)}s;animation-duration:${(2.6 + Math.random() * 3.6).toFixed(2)}s`
            frag.appendChild(star)
        }
        this.starsTarget.appendChild(frag)
    }

    // --- Lightning: random screen-glow flashes for thunderstorms ---
    _armLightning() {
        this._clearLightning()
        if (!this.hasLightningTarget) return
        const scene = sceneFor(this._family)
        if (!scene.lightning || this._reduced || this._hidden) return
        this._scheduleLightning()
    }

    _scheduleLightning() {
        const delay = 3500 + Math.random() * 7000
        this._lightningTimer = setTimeout(() => {
            this._flash()
            this._scheduleLightning()
        }, delay)
    }

    _flash() {
        if (this._hidden || this._reduced) return
        const el = this.lightningTarget
        el.classList.remove("is-flashing", "is-flashing--double")
        void el.offsetWidth
        el.classList.add(Math.random() < 0.4 ? "is-flashing--double" : "is-flashing")
    }

    _clearLightning() {
        if (this._lightningTimer) clearTimeout(this._lightningTimer)
        this._lightningTimer = null
    }

    // --- rAF loop for the canvas particle engine ---
    _syncLoop() {
        const scene = sceneFor(this._family)
        const needsCanvas = !this._reduced && !this._hidden && this._particles &&
            (scene.particles !== "none" || this._wind >= 24)
        if (needsCanvas) this._startLoop()
        else {
            this._stopLoop()
            if (this._particles) this._particles.clear()
        }
    }

    _startLoop() {
        if (this._raf) return
        this._last = performance.now()
        this._raf = requestAnimationFrame(this._tick)
    }

    _stopLoop() {
        if (this._raf) cancelAnimationFrame(this._raf)
        this._raf = null
    }

    _tick(now) {
        const dt = Math.min(0.05, (now - this._last) / 1000)
        this._last = now
        if (this._particles) this._particles.step(dt)
        this._raf = requestAnimationFrame(this._tick)
    }

    // --- Light pointer parallax on the sky layers (sun/clouds/stars) ---
    _onMove(e) {
        const rect = this.element.getBoundingClientRect()
        this._tx = (e.clientX - rect.left) / rect.width - 0.5
        this._ty = (e.clientY - rect.top) / rect.height - 0.5
        if (!this._parallaxRaf && !this._reduced && !this._hidden) {
            this._parallaxRaf = requestAnimationFrame(this._parallaxTick)
        }
    }

    _parallaxTick() {
        this._parallaxRaf = null
        this._cx += (this._tx - this._cx) * 0.08
        this._cy += (this._ty - this._cy) * 0.08
        if (this.hasLayerTarget) {
            this.layerTargets.forEach((layer) => {
                const depth = parseFloat(layer.dataset.depth || "1")
                const x = this._cx * -12 * depth
                const y = this._cy * -8 * depth
                layer.style.transform = `translate(${x.toFixed(2)}px, ${y.toFixed(2)}px)`
            })
        }
        if (Math.abs(this._tx - this._cx) > 0.001 || Math.abs(this._ty - this._cy) > 0.001) {
            this._parallaxRaf = requestAnimationFrame(this._parallaxTick)
        }
    }

    // --- Environment reactions ---
    _onVisibility() {
        this._hidden = document.hidden
        if (this._hidden) {
            this._stopLoop()
            this._clearLightning()
        } else {
            this._armLightning()
            this._syncLoop()
        }
    }

    _onMotion() {
        this._reduced = this._motionQuery.matches
        this._applyClasses()
        this._armLightning()
        this._syncLoop()
    }

    _onResize() {
        if (this._particles) this._particles.resize()
    }
}

// Re-export for callers that want the mapping without a second import.
export { familyForCode }
